/**
 * DiagnosisStateManager Component
 * Manages saving, loading, and deleting diagnosis states
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Input,
  Badge,
  cn,
} from '@roger/ui-components';
import { Save, Trash2, Loader2 } from 'lucide-react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import {
  SavedDiagnosisState,
  DiagnosisStateData,
} from '../types/diagnosisState.types';
import {
  createState,
  saveState,
  deleteState,
} from '../services/diagnosisStateService';
import { getComorbidityInfo } from '../utils/caseMix';
import { getStateNumber } from '../utils/diagnosisStateUtils';
import { refusedDiagnosesMessage } from '../utils/diagnosisCodeValidity';

interface DiagnosisStateManagerProps {
  diagnosisId: string;
  currentState: DiagnosisStateData;
  savedStates: SavedDiagnosisState[];
  activeStateId: string | null;
  onSelectState: (stateId: string) => void;
  onRefresh: () => void;
  onStateUpdated: (updatedState: DiagnosisStateData) => void;
  hasUnsavedChanges: boolean;
  hasValidationErrors?: boolean;
}

const DiagnosisStateManager: React.FC<DiagnosisStateManagerProps> = ({
  diagnosisId,
  currentState,
  savedStates,
  activeStateId,
  onSelectState,
  onRefresh,
  onStateUpdated,
  hasUnsavedChanges,
  hasValidationErrors = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  // Dialog states
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [stateToDelete, setStateToDelete] = useState<string | null>(null);

  // Input state
  const [stateName, setStateName] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const activeState = savedStates.find(s => s.id === activeStateId);
  const isOriginalState = activeState?.isOriginal;

  // Can save if there are unsaved changes
  const canSave = activeStateId && hasUnsavedChanges;

  const handleSave = () => {
    if (isOriginalState) {
      // If saving from original state, prompt for name to create new state
      setStateName('');
      setSaveDialogOpen(true);
    } else if (activeState) {
      // If updating an existing state, save directly without prompting
      handleDirectSave();
    }
  };

  // Save directly without dialog (for updating existing states)
  const handleDirectSave = async () => {
    if (!activeStateId || !activeState || isOriginalState) return;

    try {
      setSaveError(null);
      setIsSaving(true);
      // Update existing state with its current name (no renaming)
      await saveState(
        diagnosisId,
        activeStateId,
        activeState.name,
        currentState
      );
      showSuccess('Version updated successfully');
      // Update lastSavedState in parent to reflect the save without calling /data API
      onStateUpdated(currentState);
    } catch (error) {
      console.error('Error saving state:', error);
      const message =
        refusedDiagnosesMessage(error, "Can't save.") ??
        'Failed to save version. Please try again.';
      showError(message);
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  };

  // Create new state from original (with name prompt)
  const handleConfirmSave = async () => {
    if (!stateName.trim()) {
      setSaveError('Please enter a name for this version');
      return;
    }

    try {
      setSaveError(null);
      setIsSaving(true);
      // Create new state and get the response with the new state ID
      const newState = await createState(
        diagnosisId,
        stateName.trim(),
        currentState
      );
      setSaveDialogOpen(false);
      setStateName('');
      // Navigate to the new state - this will trigger data reload via URL change
      navigate(`/coding/${diagnosisId}/states/${newState.id}`, {
        state: location.state,
      });
      showSuccess('New version created successfully');
    } catch (error) {
      console.error('Error creating state:', error);
      const message =
        refusedDiagnosesMessage(error, "Can't save.") ??
        'Failed to create version. Please try again.';
      showError(message);
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (stateId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setStateToDelete(stateId);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!stateToDelete) return;

    try {
      setIsDeleting(true);
      await deleteState(diagnosisId, stateToDelete);
      setDeleteConfirmOpen(false);
      setStateToDelete(null);

      // If deleting the currently active state, redirect to original state
      if (stateToDelete === activeStateId) {
        navigate(`/coding/${diagnosisId}`, { state: location.state });
      }
      // Refresh the states list
      onRefresh();
    } catch (error) {
      console.error('Error deleting state:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className='flex items-center gap-3'>
      {/* Status Badges */}
      {hasUnsavedChanges && (
        <Badge variant='outline' className='text-yellow-700 border-yellow-400'>
          <Save className='w-3 h-3 mr-1' />
          Unsaved*
        </Badge>
      )}

      {isSaving && (
        <Badge variant='outline' className='text-blue-700 border-blue-400'>
          <Loader2 className='w-3 h-3 mr-1 animate-spin' />
          Saving...
        </Badge>
      )}

      {isDeleting && (
        <Badge variant='outline' className='text-red-700 border-red-400'>
          <Loader2 className='w-3 h-3 mr-1 animate-spin' />
          Deleting...
        </Badge>
      )}

      {/* State Selector */}
      <div className='flex items-center gap-2'>
        <label className='text-sm font-medium text-gray-700'>
          Saved Versions:
        </label>
        <Select value={activeStateId || ''} onValueChange={onSelectState}>
          <SelectTrigger className='w-[600px]'>
            <SelectValue placeholder='Select a version...' />
          </SelectTrigger>
          <SelectContent>
            {savedStates.map(state => (
              <div
                key={state.id}
                className='flex items-center justify-between group'
              >
                <SelectItem value={state.id} className='flex-1'>
                  <div className='flex items-center gap-2'>
                    {state.case_mix_data && (
                      <>
                        <Badge variant='secondary' className='text-xs'>
                          Weight: {state.case_mix_data.case_mix_weight}
                        </Badge>
                        <Badge variant='secondary' className='text-xs'>
                          Comorbidity:{' '}
                          {
                            getComorbidityInfo(
                              state.case_mix_data.comorbidity_adjustment
                            ).label
                          }
                        </Badge>
                      </>
                    )}
                    <span
                      className={cn(
                        'text-left w-[250px] line-clamp-1 font-medium',
                        state.isOriginal && 'text-muted-foreground font-medium'
                      )}
                    >
                      {(() => {
                        const stateNumber = getStateNumber(
                          state.id,
                          savedStates
                        );
                        return stateNumber
                          ? `Version ${stateNumber}: ${state.name}`
                          : state.name;
                      })()}
                    </span>
                  </div>
                </SelectItem>

                {!state.isOriginal && (
                  <Trash2
                    className='w-3 h-3 text-red-700 mx-4 cursor-pointer'
                    onClick={e => handleDeleteClick(state.id, e)}
                  />
                )}
              </div>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Action Buttons */}
      {canSave && (
        <Button
          onClick={handleSave}
          variant='default'
          disabled={hasValidationErrors}
        >
          <Save className='w-4 h-4 mr-1' />
          Save Version
        </Button>
      )}

      {/* Save Dialog - only shown when creating from original state */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save New Version</DialogTitle>
          </DialogHeader>
          <div className='space-y-4 py-4'>
            <div className='space-y-2'>
              <label className='text-sm font-medium'>Version Name</label>
              <Input
                value={stateName}
                onChange={e => setStateName(e.target.value)}
                placeholder='Enter version name...'
                autoFocus
              />
            </div>
            {saveError && (
              <div className='text-sm text-red-600'>{saveError}</div>
            )}
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Version'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Version</DialogTitle>
          </DialogHeader>
          <div className='py-4'>
            <p className='text-sm text-gray-700'>
              Are you sure you want to delete this version? This action cannot
              be undone.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setDeleteConfirmOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiagnosisStateManager;
